import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WarningIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Modal, ModalActionType } from '@/components/Modal';
import { TextField } from '@/components/TextField';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { UserAvatar } from '@/components/NavigationBar';

interface RemoveMemberModalProps {
    userId: string;
    userEmail: string;
    userName: string;
    onClose: () => void;
    onRemoved: () => void;
}

export function RemoveMemberModal({
    userId,
    userEmail,
    userName,
    onClose,
    onRemoved,
}: RemoveMemberModalProps) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const [confirmation, setConfirmation] = useState('');

    const matches = confirmation.trim() === userEmail;

    const { isSubmitting, run } = useAsyncAction(
        async () => {
            await AdminSettingsService.removeMember(userId);
            onRemoved();
        },
        {
            successMessage: t('members.removeSuccess', { name: userName }),
            errorMessage: t('members.removeFailed'),
        },
    );

    const handleSubmit = () => {
        if (matches) run();
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={
                <div className="flex flex-col gap-2">
                    <span>{t('members.removeTitle', { name: userName })}</span>
                    <div className="flex items-center gap-2.5 text-xs text-grey-200 font-normal">
                        <UserAvatar name={userName} size="xs" />
                        <span>{userName}</span>
                    </div>
                </div>
            }
            onCancel={onClose}
            onSubmit={handleSubmit}
            submitText={t('members.removeSubmit')}
            submitDisabled={!matches || isSubmitting}
            submitInProgress={isSubmitting}
            actionType={ModalActionType.DANGER}
            size="small"
        >
            <div className="flex flex-col gap-4 px-6 py-6">
                <div className="flex items-start gap-2 p-3 rounded-md border border-danger-300/40 bg-danger-300/10">
                    <WarningIcon className="size-4 text-danger-200 shrink-0 mt-0.5" />
                    <p className="text-sm text-danger-200">
                        {t('members.removeWarning')}
                    </p>
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="text-sm-medium text-white-100">
                        {t('members.removeConfirmLabel', {
                            email: userEmail,
                        })}
                    </label>
                    <TextField
                        value={confirmation}
                        onChange={(event) =>
                            setConfirmation(event.target.value)
                        }
                        placeholder={userEmail}
                        autoFocus
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && matches)
                                handleSubmit();
                            if (event.key === 'Escape') onClose();
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
}
